import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from '../../../features/auth/utils/authStorage';
import Button from '../../../shared/components/ui/Button';
import JobCard from '../components/JobCard';
import FilterChips from '../components/FilterChips';
import useMyFeed from '../../../features/jobs/hooks/useMyFeed';
import useAppliedJobs from '../../../features/jobs/hooks/useAppliedJobs';
import { getTimeBasedGreeting } from '../../../utils/getTimeBasedGreeting';
import CardIcon from '../../../shared/components/icons/CardIcon';

function StatCard({ label, value, icon }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="flex items-center gap-2 text-xs font-semibold text-slate-500"><CardIcon name={icon} />{label}</p>
      <p className="mt-2 text-2xl font-bold text-navy">{value}</p>
    </div>
  );
}

function Home() {
  const navigate = useNavigate();
  const auth = getAuth();
  const firstName = auth?.user?.firstName || 'there';
  const { greeting, icon } = getTimeBasedGreeting();

  const [activeFilter, setActiveFilter] = React.useState('town');
  const { jobs, loading, error } = useMyFeed(activeFilter);
  const { jobs: appliedJobs } = useAppliedJobs();

  // Get IDs of applied jobs to exclude them from feed
  const appliedJobIds = React.useMemo(() => {
    return appliedJobs.map((job) => job.id);
  }, [appliedJobs]);

  // Filter jobs to exclude applied ones and apply filter-specific logic
  const filteredJobs = React.useMemo(() => {
    let result = jobs.filter((job) => {
      // Exclude if already applied (check both ways)
      if (appliedJobIds.includes(job.id) || job.hasApplied) {
        return false;
      }
      return true;
    });

    if (activeFilter === 'urgent') {
      result = result.filter((job) => job.urgency === 'urgent');
    } else if (activeFilter === 'highpay') {
      result = result.sort((a, b) => b.budget - a.budget);
    }

    return result;
  }, [jobs, activeFilter, appliedJobIds]);

  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-white p-6 sm:p-8 shadow-sm">
        <p className="text-sm font-semibold text-slate-500">Welcome</p>
        <h1 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-navy">
          {greeting}, {firstName} <CardIcon name={icon} className="inline-block h-6 w-6 align-middle" />
        </h1>
        <p className="mt-2 text-sm text-slate-600">Ready to find work today?</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Button variant="primary" onClick={() => navigate('/worker/find-jobs')}>
            Find Jobs
          </Button>
          <Button variant="ghost" onClick={() => navigate('/worker/profile')}>
            Complete Profile
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Applications" value="" icon="application" />
        <StatCard label="Hired" value="" icon="briefcase" />
        <StatCard label="Rating" value="" icon="star" />
      </div>

      <FilterChips
        filters={[
          { label: 'Nearby jobs', value: 'town' },
          { label: 'Urgent', value: 'urgent' },
          { label: 'High pay', value: 'highpay' },
        ]}
        active={activeFilter}
        onChange={setActiveFilter}
      />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold text-navy"><CardIcon name="briefcase" />Jobs feed</h2>
          <button
            type="button"
            onClick={() => navigate('/worker/find-jobs')}
            className="text-sm font-semibold text-navy hover:underline"
          >
            View all
          </button>
        </div>
        <div className="grid gap-4">
          {loading && (
            <div className="rounded-3xl bg-white p-8 text-center text-slate-600 shadow-sm">
              Loading jobs...
            </div>
          )}
          {error && (
            <div className="rounded-3xl bg-rose-50 p-8 text-center text-rose-700 shadow-sm">
              Error: {error}
            </div>
          )}
          {!loading && filteredJobs.length === 0 && (
            <div className="rounded-3xl bg-white p-8 text-center text-slate-500 shadow-sm">
              No jobs found for this filter.
            </div>
          )}
          {!loading && filteredJobs.length > 0 && filteredJobs.map((job) => (
            <JobCard key={job.id} job={job} />
          ))}
        </div>
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-navy">Recent activity</h2>
        <p className="mt-2 text-sm text-slate-600">Your recent activity will show here.</p>
      </div>
    </div>
  );
}

export default Home;

